import type { WellCompletionRow } from 'types/well-completions';
import type { PerforationRow } from 'types/well-perforations';

/**
 * One completion's worth of perforation intervals, collapsed into a single
 * summary row for the outer table.
 *
 * A UWI14 does NOT imply a single completion — the same `uwi14Completion` can
 * carry several `completionObsNo` values (well 4902705479's …0001 has both
 * completion 1 and completion 2). So identity here is the (UWI14, Completion #)
 * pair, not the UWI14 alone.
 */
export interface PerforationGroupRow extends Record<string, unknown> {
  /**
   * Stable identity for the outer table's row id. Two groups can share the same
   * displayed `uwi14Completion`, so the row id must key on this composite — not
   * on `uwi14Completion`, which would collapse them into one row.
   */
  groupKey: string;
  uwi14Completion: string;
  completionObsNo: number | null;
  intervalCount: number;
  /**
   * Highest Stage # among the group's intervals — the completion's stage count
   * when stages are numbered 1..N, which is the normal case. Deliberately a max
   * rather than a distinct count, so a completion whose perfs record stages 5
   * and 7 reports 7, not 2. Null when no interval carries a stage number.
   */
  maxStageNum: number | null;
  /** Shallowest interval's Top Depth MD (ft) in the group. */
  minTopDepthFt: number | null;
  /** Deepest interval's Base Depth MD (ft) in the group. */
  maxBaseDepthFt: number | null;
  /** Child interval rows, pre-sorted by Top Depth MD ascending. */
  intervals: PerforationRow[];
  /** The completion's date from the completions read, when it has one. */
  completionDate: string | null;
}

/**
 * Ascending numeric comparator that sorts nulls last — used for both interval
 * depths and completion numbers. Written as explicit branches rather than
 * `(a ?? Infinity) - (b ?? Infinity)` because that subtraction yields `NaN`
 * when BOTH values are null, and a comparator returning NaN makes the sort
 * order implementation-defined per spec. V8 tolerates it in practice, so this
 * is hardening rather than a bug fix.
 *
 * @param {number | null} a - The left value, or null when unrecorded.
 * @param {number | null} b - The right value, or null when unrecorded.
 * @return {number} Negative, zero, or positive per the comparator contract.
 */
function byNumberAscNullsLast(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * The completion-summary row order: UWI14 ascending, then Completion #
 * ascending within a UWI14 (per Dustin/Eira's UAT feedback — reading the table
 * well-by-well beats reading it depth-by-depth, since a well's completions were
 * previously scattered across the list by their depths).
 *
 * `initialTableState.sortColumn` is single-column, so the second key can't be
 * expressed there. It doesn't need to be: the table's default sort is UWI14
 * ascending, and TanStack sorts this pre-ordered array with a stable sort, so
 * ties (one UWI14's several completions) keep the completion order set here.
 * Verified by removing it and watching the completion-order table test still
 * pass — an explicit `sortingFn` on the column guaranteed nothing.
 *
 * UWI14s are fixed-width digit strings, so `numeric: true` is belt-and-braces
 * against a short or suffixed value comparing lexicographically.
 *
 * @param {PerforationGroupRow} a - The left completion summary.
 * @param {PerforationGroupRow} b - The right completion summary.
 * @return {number} Negative, zero, or positive per the comparator contract.
 */
function byUwi14ThenCompletionAsc(
  a: PerforationGroupRow,
  b: PerforationGroupRow
): number {
  const byUwi14 = a.uwi14Completion.localeCompare(
    b.uwi14Completion,
    undefined,
    { numeric: true }
  );
  if (byUwi14 !== 0) return byUwi14;
  return byNumberAscNullsLast(a.completionObsNo, b.completionObsNo);
}

/**
 * Composite group identity: a UWI14 can carry more than one completion, so the
 * completion number is part of the key. Grouping on `uwi14Completion` alone
 * merges distinct completions into one row and interleaves their intervals by
 * depth.
 *
 * @param {object} row - A perforation-interval or completion row.
 * @return {string} The `uwi14Completion::completionObsNo` group key.
 */
function groupKeyFor(
  row: Pick<PerforationRow, 'uwi14Completion' | 'completionObsNo'>
): string {
  return `${row.uwi14Completion}::${row.completionObsNo}`;
}

/**
 * Groups flat perforation-interval rows by completion — the (UWI14,
 * Completion #) pair — ordering the groups by UWI14 then Completion # ascending
 * and each group's intervals by Top Depth MD ascending (nulls last). Pure: no
 * React, no fetching, so the table and its tests read one computation.
 *
 * The interval sort is kept even though the endpoint already returns rows in
 * `top_depth asc` order (see {@link useWellPerforations}) and partitioning
 * preserves that within a group. It's O(n log n) on a handful of rows, and it
 * keeps this function's output contract independent of fetch order — otherwise
 * a BE default-sort change would silently reorder every nested table.
 *
 * @param {PerforationRow[]} rows - Flat perforation-interval rows for a well.
 * @return {PerforationGroupRow[]} One row per completion, each carrying its
 *   sorted child intervals.
 */
export function groupPerforationsByCompletion(
  rows: PerforationRow[]
): PerforationGroupRow[] {
  const groupsByKey = new Map<string, PerforationRow[]>();
  for (const row of rows) {
    const key = groupKeyFor(row);
    const existing = groupsByKey.get(key);
    if (existing) {
      existing.push(row);
    } else {
      groupsByKey.set(key, [row]);
    }
  }

  const groups: PerforationGroupRow[] = Array.from(groupsByKey.entries()).map(
    ([groupKey, intervals]) => {
      const topDepths = intervals
        .map((r) => r.perfTopDepthFt)
        .filter((v): v is number => v !== null);
      const baseDepths = intervals
        .map((r) => r.perfBaseDepthFt)
        .filter((v): v is number => v !== null);
      const stageNums = intervals
        .map((r) => r.stageNum)
        .filter((v): v is number => v !== null);

      // Every member of a group shares both key parts by construction, so the
      // first row's values are the group's values.
      return {
        groupKey,
        uwi14Completion: intervals[0].uwi14Completion,
        completionObsNo: intervals[0].completionObsNo,
        intervalCount: intervals.length,
        maxStageNum: stageNums.length ? Math.max(...stageNums) : null,
        minTopDepthFt: topDepths.length ? Math.min(...topDepths) : null,
        maxBaseDepthFt: baseDepths.length ? Math.max(...baseDepths) : null,
        completionDate: null,
        intervals: [...intervals].sort((a, b) =>
          byNumberAscNullsLast(a.perfTopDepthFt, b.perfTopDepthFt)
        ),
      };
    }
  );

  groups.sort(byUwi14ThenCompletionAsc);

  return groups;
}

/**
 * Puts each completion's date on its summary row. A completion with no row in
 * the completions read keeps a null date. When the read holds more than one row
 * for a completion, the newest row that has a date wins, so a dateless
 * duplicate never blanks a known date.
 *
 * @param {PerforationGroupRow[]} groups - The completion summary rows.
 * @param {WellCompletionRow[]} completions - The well's completions.
 * @return {PerforationGroupRow[]} The same rows, each with its date.
 */
export function withCompletionDates(
  groups: PerforationGroupRow[],
  completions: WellCompletionRow[]
): PerforationGroupRow[] {
  // The read is ordered by id, so a later row is a newer one.
  const dates = new Map(
    completions
      .filter((completion) => completion.completionDate)
      .map((completion) => [groupKeyFor(completion), completion.completionDate])
  );
  return groups.map((group) => ({
    ...group,
    completionDate: dates.get(group.groupKey) ?? null,
  }));
}
